import { CommonModule } from "@angular/common";
import { Component, computed, effect, inject, linkedSignal, model, signal, TemplateRef, untracked, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzCardComponent } from "ng-zorro-antd/card";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzGridModule } from "ng-zorro-antd/grid";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzSelectModule } from "ng-zorro-antd/select";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { rxResource, takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { catchError, EMPTY, filter, map, take, tap } from "rxjs";
import { Job } from "../../../models/job";
import { Project } from "../../../models/project";
import { NzModalService } from "ng-zorro-antd/modal";
import { NzNotificationService } from "ng-zorro-antd/notification";
import {
  FileMeta,
  FileSpec,
  JOB_TYPE_CONFIGS,
  JOB_TYPE_OPTIONS,
  toFileMeta,
} from "./job-type-config";
import { FileAssignmentComponent } from "./file-assignment/file-assignment.component";
import { FileEditorComponent } from "./file-editor/file-editor.component";
import { JobConfigComponent } from "./job-config/job-config.component";
import { lintEnvSh, lintRunfile } from "../../../shared/monaco-editor/editor-lint";

interface JobTemplate {
  bucket: string;
  prefix: string;
  xwi_prefix: string;
  runfiles: string[];
  all_files: string[];
}

@Component({
  selector: "app-job-submission",
  templateUrl: "./job-submission.component.html",
  styleUrl: "./job-submission.component.less",
  imports: [
    CommonModule,
    FormsModule,
    NzCardComponent,
    NzFormModule,
    NzSelectModule,
    NzButtonModule,
    NzGridModule,
    NzInputModule,
    FileAssignmentComponent,
    FileEditorComponent,
    JobConfigComponent,
  ],
})
export class JobSubmissionComponent {
  private cachedProjectService = inject(CachedProjectService);
  private nzNotificationService = inject(NzNotificationService);
  private modalService = inject(NzModalService);

  @ViewChild('confirmSubmitTpl') private confirmSubmitTpl!: TemplateRef<any>;

  protected readonly jobTypeOptions = JOB_TYPE_OPTIONS;

  protected currentProject$ = this.cachedProjectService.currentProject.pipe(
    takeUntilDestroyed(),
    filter((p) => !!p)
  );
  protected projectJobs = toSignal(this.currentProject$.pipe(
    takeUntilDestroyed(),
    map((project) => project?.jobs || []),
    filter((jobs) => jobs.length > 0)
  ));

  protected templateJob = model<Job | null>(null);
  protected editedRunfileContent = signal<string>("");
  protected editedEnvShContent = signal<string>("");
  protected selectedJobType = signal<string>('fwi');
  // protected jobPath = signal<string>('');
  protected jobPathTouched = signal<boolean>(false);
  protected jobConfigValues = signal<Record<string, any> | null>(null);
  protected jobConfigValid = signal<boolean>(false);
  protected submitting = signal<boolean>(false);
  protected prepping = signal<boolean>(false);
  protected currentProject = signal<Project | null>(null);

  /** Populated by FileAssignmentComponent via model() binding */
  protected resolvedSelections = signal<Record<string, FileMeta>>({});
  protected resolvedRenames = signal<Record<string, string>>({});

  protected jobTemplateResource = rxResource<JobTemplate, Job | undefined>({
    params: computed(() => this.templateJob() ?? undefined),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      this.nzNotificationService.info("", "Fetching job template...");
      return this.cachedProjectService.getJobTemplate(params.id).pipe(
        catchError(() => {
          this.nzNotificationService.remove();
          this.nzNotificationService.error("Error", "Failed to fetch job template");
          return EMPTY;
        }),
        tap(() => {
          this.nzNotificationService.remove();
          this.nzNotificationService.success("", "Job template fetched!");
        })
      );
    },
  });

  protected allFiles = computed<FileMeta[]>(
    () => (this.jobTemplateResource.value()?.all_files ?? []).map(toFileMeta)
  );
  protected runfiles = computed<FileMeta[]>(
    () => (this.jobTemplateResource.value()?.runfiles ?? []).map(toFileMeta)
  );
  protected templateFilesBasePath = computed<string>(() => {
    const t = this.jobTemplateResource.value();
    if (!t) return '';
    return `${t.bucket}/${t.prefix}`.replace(/\/+$/, '');
  });
  protected xwiPrefixForFiles = computed<string>(() =>
    this.jobTemplateResource.value()?.xwi_prefix ?? ''
  );

  protected currentFileSpecs = computed<FileSpec[]>(
    () => JOB_TYPE_CONFIGS[this.selectedJobType()]?.files ?? []
  );

  protected runfileLintIssues = computed(() => lintRunfile(this.editedRunfileContent()));
  protected envShLintIssues = computed(() => lintEnvSh(this.editedEnvShContent()));
  protected jobPathHasSpaces = computed(() => /\s/.test(this.jobPath()));

  protected canSubmit = computed(() => {
    if (!this.jobPath().trim()) return false;
    if (this.jobPathHasSpaces()) return false;
    if (!this.jobConfigValid()) return false;
    const resolved = this.resolvedSelections();
    return this.currentFileSpecs()
      .filter((s) => s.required)
      .every((s) => resolved[s.key]);
  });

  protected jobPath = linkedSignal(() => {
    const parent = this.parentPath()
    const jobName = this.extractNextJobName()
    if (!parent) return '';
    return `${this.parentPath()}/${jobName}`;
  });
  private parentPath = signal<string>("");

  constructor() {
    this.currentProject$.subscribe((p) => {
      this.currentProject.set(p);
    });

    effect(() => {
      this.templateJob();
      this.resolvedSelections.set({});
    });

    // When a new template is selected, seed the job path from its prefix + current job_name
    effect(() => {
      const template = this.jobTemplateResource.value();
      if (template){
        const {prefix, bucket} = template;
        const basePath = this.getParentPath(bucket + "/" + prefix);
        untracked(() => {
          this.parentPath.set(basePath);
        });
      }
    });
  }

  private extractNextJobName(): string {
    const templateJob = this.templateJob()
    const jobs = this.projectJobs()

    if (!templateJob || !jobs) return ""

    const templateMatch = templateJob.name.split(/[-_]/)[0].match(/([a-zA-Z]*)(\d+)/)
    if (!templateMatch) return ""
    const [, templateSeries] = templateMatch

    const sortedJobs = jobs.map(j => {
      const match = j.name.split(/[-_]/)[0].match(/([a-zA-Z]*)(\d+)/)
      if (!match) return null

      return {
        name: j.name,
        series: match[1],
        number: Number(match[2])
      }
    }).filter((item): item is { name: string; series: string; number: number } => item !== null)
      .sort((a, b) => b.number - a.number)

    const finalist = sortedJobs.find(s => s.series === templateSeries)
    if (!finalist) return `${templateSeries}001-`

    return `${finalist.series}${String(finalist.number + 1).padStart(3, "0")}-`
  }

  private getParentPath(path: string): string {
    const trimmed = path.replace(/\/+$/, '');
    const lastSlash = trimmed.lastIndexOf('/');
    return lastSlash >= 0 ? trimmed.substring(0, lastSlash) : trimmed;
  }

  protected submit(): void {
    if (!this.currentProject()) return;
    this.openActionModal('submit-job');
  }

  protected prep(): void {
    // here before we send the payload, we assign the job name in the config
    let configName = this.jobPath().split('/').pop()!.split(/[_-]/, 1)[0].replace(/[-_]$/, "")
    this.updateJobConfigName(configName.length > 6 ? configName.slice(0, 6) : configName)

    if (!this.currentProject()) return;
    this.openActionModal('prep-job');
  }

  protected updateJobConfigName(value: string): void {
    this.jobConfigValues.update(config => ({ ...(config ?? {}), job_name: value }));
  }

  private openActionModal(action: 'submit-job' | 'prep-job'): void {
    const label = action === 'submit-job' ? 'Submit Job' : 'Prep Job';
    this.modalService.confirm({
      nzTitle: `Confirm: ${label}`,
      nzContent: this.confirmSubmitTpl,
      nzOkText: label,
      nzOkType: 'primary',
      nzCancelText: 'Cancel',
      nzWidth: 520,
      nzOnOk: () => this.doAction(action),
    });
  }

  private doAction(action: 'submit-job' | 'prep-job'): void {
    const projectId = this.currentProject()?.id;
    if (!projectId) return;

    const jobConfig =  Object.fromEntries(Object.entries(this.jobConfigValues() ?? {}).filter(([, v]) => v !== null))

    const payload = {
      template_job_id: this.templateJob()?.id,
      job_type: this.selectedJobType(),
      job_path: this.jobPath(),
      files: Object.fromEntries(
        Object.entries(this.resolvedSelections()).map(([k, v]) => [
          k, { ...v, new_name: this.resolvedRenames()[k] || undefined }
        ])
      ),
      runfile_content: this.editedRunfileContent(),
      env_sh_content: this.editedEnvShContent(),
      job_config: jobConfig,
    };

    const isSubmit = action === 'submit-job';
    if (isSubmit) this.submitting.set(true); else this.prepping.set(true);

    const call = isSubmit
      ? this.cachedProjectService.submitJob(projectId, payload)
      : this.cachedProjectService.prepJob(projectId, payload);

    call.pipe(take(1)).subscribe({
      next: (response) => {
        if (isSubmit) this.submitting.set(false); else this.prepping.set(false);
        const label = isSubmit ? 'Job Submitted' : 'Job Prepared';
        this.nzNotificationService.success(label, response?.message ?? `${label} successfully.`);
        this.resetForm();
      },
      error: (err) => {
        if (isSubmit) this.submitting.set(false); else this.prepping.set(false);
        const detail = err?.error?.message ?? err?.error?.detail ?? err?.message ?? 'An unexpected error occurred.';
        this.nzNotificationService.error(`${isSubmit ? 'Submission' : 'Prep'} Failed`, detail);
      },
    });
  }

  private resetForm(): void {
    this.templateJob.set(null);
    this.jobPath.set('');
    this.jobPathTouched.set(false);
    this.editedRunfileContent.set('');
    this.editedEnvShContent.set('');
    this.jobConfigValues.set(null);
    this.jobConfigValid.set(false);
    this.resolvedSelections.set({});
    this.resolvedRenames.set({});
    this.parentPath.set('');
  }
}
